'use client';

import { CellContext, ColumnDef, HeaderContext } from '@tanstack/react-table';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { BalanceRowData, BalancesTableMeta } from './cells/types';
import {
	AssetCell,
	BalanceCell,
	EarningApyCell,
	BorrowApyCell,
	LiqPriceCell,
	EarningsCell,
	ActionsCell,
} from './cells';

function getMeta(
	context:
		| CellContext<BalanceRowData, unknown>
		| HeaderContext<BalanceRowData, unknown>
): BalancesTableMeta {
	return context.table.options.meta as BalancesTableMeta;
}

interface GetBalancesColumnsOptions {
	isAmplifyAccount: boolean;
	location: 'trade-page' | 'portfolio-page';
}

export function getBalancesColumns({
	isAmplifyAccount,
	location,
}: GetBalancesColumnsOptions): ColumnDef<BalanceRowData>[] {
	const isPortfolioPage = location === 'portfolio-page';

	const columns: ColumnDef<BalanceRowData>[] = [
		{
			id: 'asset',
			header: 'Asset',
			meta: {
				width: { ratio: 20, minPx: 100 },
				bodyCellClassName: 'first:xl:pl-md',
				headerCellClassName: 'first:xl:pl-md',
			},
			cell: (context) => {
				const { handleSelection } = getMeta(context);
				return (
					<AssetCell
						row={context.row.original}
						handleSelection={handleSelection}
					/>
				);
			},
		},
		{
			id: 'balance',
			header: 'Balance',
			meta: { width: { ratio: 20, minPx: 100 } },
			cell: ({ row }) => <BalanceCell row={row.original} />,
		},
		{
			id: 'earningApy',
			header: 'Earning APY',
			meta: { width: { ratio: 20, minPx: 100 } },
			cell: ({ row }) => <EarningApyCell row={row.original} />,
		},
		{
			id: 'borrowApy',
			header: 'Borrow APY',
			meta: { width: { ratio: 20, minPx: 90 } },
			cell: ({ row }) => <BorrowApyCell row={row.original} />,
		},
	];

	if (isPortfolioPage) {
		columns.push({
			id: 'earnings',
			header: '90D Earnings',
			meta: { width: { ratio: 20, minPx: 100 } },
			cell: ({ row }) => <EarningsCell row={row.original} />,
		});
	} else {
		columns.push({
			id: 'liqPrice',
			meta: { width: { ratio: 20, minPx: 100 } },
			header: () => (
				<DefinitionTooltip
					definitionId="balancesLiquidationPrice"
					className="capitalize"
				>
					Liq. Price
				</DefinitionTooltip>
			),
			cell: ({ row }) => <LiqPriceCell row={row.original} />,
		});
	}

	columns.push({
		id: 'actions',
		header: '',
		meta: {
			width: { fixedPx: 164 },
			bodyCellClassName: 'xl:pr-md',
			headerCellClassName: 'xl:pr-md',
		},
		cell: ({ row }) =>
			isAmplifyAccount ? null : <ActionsCell row={row.original} />,
	});

	return columns;
}
